import { SearchInput } from "@patternfly/react-core";
import { DualListSelectorComposable, type Option } from "@rhc-shared-components/dual-list-selector";
import { useQuery } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { type FC, type MutableRefObject, useEffect, useMemo, useState } from "react";
import { ProductListingTypes } from "../../../constants/product";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import type { ProjectListGraphQLResponseDataEntity } from "../../../interfaces/graphql";
import { CreateFormKeys } from "../CreateComponentsModal/createConstants";

interface DualListSelectorWrapperProps {
	searchHandler: (value: string, executeImmediately?: boolean) => void;
	getFilterQuery: (searchValue?: string, selectedComponent?: ProjectListGraphQLResponseDataEntity[]) => string;
	componentCache: MutableRefObject<{ [key: string]: ProjectListGraphQLResponseDataEntity }>;
	debouncedSetQuery: (value: string) => void;
	query: string;
}

export const DualListSelectorWrapper: FC<DualListSelectorWrapperProps> = ({
	searchHandler,
	getFilterQuery,
	componentCache,
	debouncedSetQuery,
	query,
}) => {
	const [searchValue, setSearchValue] = useState("");
	const { data: attachableProjects, isFetching: isAttachableProjectsApiFetching } = useQuery<{
		data?: ProjectListGraphQLResponseDataEntity[];
	}>({
		queryKey: ["attachableComponents", query],
		queryFn: () => {
			// TODO: remove later
			return { data: [] };
		},
		enabled: false,
	});
	const { productDetail, certificationListState } = useProductMetadata();
	const { values } = useFormikContext<Record<string, Option[] | string>>();
	const { chosenOptions, availableOptions } = useMemo(() => {
		const availableOptionsArr = [] as Option[];
		//filtering available options based on formik context value
		const certProjects = values[CreateFormKeys.certProjects] as Option[] | undefined;
		const filteredAvailableOptions = attachableProjects?.data?.filter(
			(elm: ProjectListGraphQLResponseDataEntity) =>
				!certProjects?.some((item: Option) => item.text.substring(item.text.lastIndexOf("-") + 1).trim() === elm?._id),
		);
		filteredAvailableOptions?.forEach((item: ProjectListGraphQLResponseDataEntity) => {
			const id = item?._id;
			componentCache.current[id] = item;
			availableOptionsArr.push({
				text: `${item?.name} - ${item?._id}`,
				selected: false,
				isVisible: true,
				isDisabled: false,
			});
		});

		return {
			availableOptions: availableOptionsArr,
			chosenOptions: values[CreateFormKeys.certProjects] as Option[],
		};
	}, [attachableProjects, componentCache, values]);

	const filterQuery = useMemo(() => {
		const selectedProjects = values[CreateFormKeys.certProjects];
		if (productDetail.type === ProductListingTypes.OpenStackInfrastructure || !selectedProjects) {
			return null;
		}
		const result = (selectedProjects as Option[])
			?.map((item: Option) => item.text.substring(item.text.lastIndexOf("-") + 1).trim())
			?.map((id: string) => {
				// Check for selectedProjects in certificationListState as well if not found in componentCache
				const cachedItem = componentCache.current?.[id];
				if (cachedItem) {
					return cachedItem;
				}
				return certificationListState?.find((certItem) => certItem?._id === id);
			})
			.filter(
				(item: ProjectListGraphQLResponseDataEntity | undefined): item is ProjectListGraphQLResponseDataEntity =>
					!!item,
			);
		return getFilterQuery(searchValue, result);
	}, [values, componentCache, getFilterQuery, searchValue, productDetail, certificationListState]);

	useEffect(() => {
		if (filterQuery) {
			debouncedSetQuery(filterQuery);
		}
	}, [filterQuery, debouncedSetQuery]);

	return (
		<DualListSelectorComposable
			availableOptions={availableOptions}
			chosenOptions={chosenOptions}
			availableSelectionPanelTitle={"Available components"}
			chosenSelectionPanelTitle={"Chosen components"}
			isFetching={isAttachableProjectsApiFetching}
			searchInput={
				<SearchInput
					id="available-components-search"
					name="available-components-search"
					value={searchValue}
					onChange={(_event, value) => {
						setSearchValue(value);
						searchHandler(value);
					}}
					onClear={() => {
						setSearchValue("");
						searchHandler("", true);
					}}
				/>
			}
			name={CreateFormKeys.certProjects}
			searchAvailableValue={searchValue}
		/>
	);
};
